import { signal } from '@preact/signals';
import { getZone } from '../../content';
import { RIFT_ROSTER } from '../../content/monsters';
import type { Game } from '../../game/Game';
import { rollLoot } from '../../game/loot';
import type { ZoneRuntime, ZoneScript } from '../../game/ZoneRuntime';
import type { V2 } from '../../core/math';
import { EVENTS, type Occurrence, fmtCountdown, occurrence, relevant } from './schedule';

/** Banner shown under the minimap while an event runs (or is about to) in this zone. */
export const eventBanner = signal<{ title: string; detail: string; tone: 'warn' | 'live' | 'done' } | null>(null);

export const BREACH_WAVES = [8, 10, 12];
const BREACH_RADIUS = 45;
const ELITE = RIFT_ROSTER.elites[0] ?? 'hollow_knight';

interface BreachState {
  start: number;
  /** Waves spawned so far (0 before the first). */
  wave: number;
  done: boolean;
  failed: boolean;
}

/** Where the breach opens: the monster pack closest to the middle of the zone. */
export function breachPoint(z: ZoneRuntime): V2 & { level: number } {
  const L = z.layout;
  const cx = L.minX + L.width / 2;
  const cz = L.minZ + L.depth / 2;
  let best: (V2 & { level: number }) | null = null;
  let bd = Infinity;
  for (const p of L.packs) {
    if (p.tags?.includes('fieldBoss')) continue;
    const d = Math.hypot(p.x - cx, p.z - cz);
    if (d < bd) {
      bd = d;
      best = { x: p.x, z: p.z, level: p.level };
    }
  }
  const sp = L.spawnPoints.start ?? { x: cx, z: cz };
  return best ?? { x: sp.x, z: sp.z, level: L.levelRange[0] };
}

/**
 * World events in one field zone. Every peer announces from the shared
 * schedule; the host spawns field bosses and breach waves and mirrors the
 * breach state; everyone rolls their own rewards.
 */
export class WorldEventScript implements ZoneScript {
  private announced = new Set<string>();
  private spawned = new Set<string>();
  private breach: BreachState | null = null;
  private breachWave = new Set<number>();
  private checkT = 0;

  constructor(
    private game: Game,
    private now: () => number = () => Date.now(),
  ) {}

  start(z: ZoneRuntime): void {
    if (z.world.isHost) this.blockFieldBossPacks(z);
    this.update(z);
  }

  hostChanged(z: ZoneRuntime): void {
    // Migration rebuilt pack timers: field bosses stay on the calendar.
    if (z.world.isHost) this.blockFieldBossPacks(z);
  }

  /** Field bosses only appear on schedule (their packs never respawn by themselves). */
  private blockFieldBossPacks(z: ZoneRuntime): void {
    for (const st of z.packs) {
      if (!st.def.tags?.includes('fieldBoss')) continue;
      for (const id of st.members) z.world.removeActor(id);
      st.members = [];
      st.respawnAt = Infinity;
    }
  }

  tick(z: ZoneRuntime, dt: number): void {
    this.checkT -= dt;
    if (this.checkT > 0) return;
    this.checkT = 1;
    this.update(z);
  }

  private update(z: ZoneRuntime): void {
    const now = this.now();
    let banner: typeof eventBanner.value = null;
    for (const o of relevant(z.layout.id, now)) {
      this.announce(o, now);
      banner = o.def.kind === 'fieldBoss' ? this.updateFieldBoss(z, o, now) : this.updateBreach(z, o, now);
    }
    eventBanner.value = banner;
    if (z.world.isHost) this.retireExpired(z, now);
  }

  private announce(o: Occurrence, now: number): void {
    const hud = this.game.hud;
    const warnKey = `${o.def.id}:${o.start}:warn`;
    const startKey = `${o.def.id}:${o.start}:start`;
    if (!o.active && !this.announced.has(warnKey)) {
      this.announced.add(warnKey);
      hud.announce(`${o.def.name} — ${fmtCountdown(o.start - now)}`, 'warning');
    } else if (o.active && !this.announced.has(startKey)) {
      this.announced.add(startKey);
      hud.announce(o.def.kind === 'fieldBoss' ? `${o.def.name} has appeared!` : 'An Aether Breach tears open!', 'boss');
    }
  }

  private eventTag(o: Occurrence): string {
    return `event:${o.def.id}:${o.start}`;
  }

  private updateFieldBoss(z: ZoneRuntime, o: Occurrence, now: number): typeof eventBanner.value {
    const tag = this.eventTag(o);
    const boss = [...z.world.actors.values()].find((a) => a.monster?.tags.includes(tag));
    if (!o.active) return { title: o.def.name, detail: `Appears in ${fmtCountdown(o.start - now)}`, tone: 'warn' };
    if (z.world.isHost && !this.spawned.has(tag) && !boss && o.def.boss) {
      this.spawned.add(tag);
      const pack = z.layout.packs.find((p) => p.id === o.def.packId);
      const at = pack ? { x: pack.x, z: pack.z } : breachPoint(z);
      z.spawnMonster(o.def.boss, pack?.level ?? o.def.level, at, { tags: ['fieldBoss', 'worldEvent', tag], facing: Math.PI });
    }
    if (boss && !boss.alive) return { title: o.def.name, detail: 'Defeated', tone: 'done' };
    return { title: o.def.name, detail: `${fmtCountdown(o.end - now)} left`, tone: 'live' };
  }

  /** Host: event bosses that outlive their window leave; stale breach spawn is cleaned up. */
  private retireExpired(z: ZoneRuntime, now: number): void {
    for (const a of [...z.world.actors.values()]) {
      const tag = a.monster?.tags.find((t) => t.startsWith('event:'));
      if (!tag || !a.alive) continue;
      const [, id, start] = tag.split(':');
      const def = EVENTS.find((d) => d.id === id);
      if (!def || now < Number(start) + def.durationMin * 60_000) continue;
      // Don't yank a boss mid-fight; it leaves once it has calmed down.
      if (a.monster && a.monster.idle < 8) continue;
      z.world.removeActor(a.id);
    }
  }

  // ---------------------------------------------------------------- Aether Breach

  private updateBreach(z: ZoneRuntime, o: Occurrence, now: number): typeof eventBanner.value {
    if (!o.active) return { title: o.def.name, detail: `Opens in ${fmtCountdown(o.start - now)}`, tone: 'warn' };
    if (!this.breach || this.breach.start !== o.start) this.breach = { start: o.start, wave: 0, done: false, failed: false };
    const b = this.breach;
    if (z.world.isHost && !b.done && !b.failed) {
      const living = [...this.breachWave].filter((id) => z.world.actors.get(id)?.alive).length;
      if (living === 0) {
        if (b.wave < BREACH_WAVES.length) this.spawnWave(z, b);
        else this.finishBreach(z, b, true);
      }
      if (now >= o.end - 1000 && !b.done) this.finishBreach(z, b, false);
    }
    if (b.done) return { title: o.def.name, detail: 'Sealed — well fought!', tone: 'done' };
    if (b.failed) return { title: o.def.name, detail: 'The breach closed', tone: 'done' };
    return { title: o.def.name, detail: `Wave ${Math.max(1, b.wave)}/${BREACH_WAVES.length} · ${fmtCountdown(o.end - now)}`, tone: 'live' };
  }

  private spawnWave(z: ZoneRuntime, b: BreachState): void {
    const at = breachPoint(z);
    const count = BREACH_WAVES[b.wave] ?? 8;
    this.breachWave.clear();
    for (let i = 0; i < count; i++) {
      const ang = (i / count) * Math.PI * 2;
      const r = 5 + (i % 3) * 3;
      const p = z.nav.nearestWalkable({ x: at.x + Math.sin(ang) * r, z: at.z + Math.cos(ang) * r }) ?? at;
      const def = RIFT_ROSTER.swarm[i % RIFT_ROSTER.swarm.length] ?? 'rift_imp';
      this.breachWave.add(z.spawnMonster(def, at.level, p, { tags: ['breach'] }).id);
    }
    if (b.wave === BREACH_WAVES.length - 1) this.breachWave.add(z.spawnMonster(ELITE, at.level + 1, at, { tags: ['breach', 'elite'] }).id);
    b.wave++;
    this.sync(z);
  }

  private finishBreach(z: ZoneRuntime, b: BreachState, won: boolean): void {
    b.done = won;
    b.failed = !won;
    if (!won) for (const id of this.breachWave) if (z.world.actors.get(id)?.alive) z.world.removeActor(id);
    this.sync(z);
    this.onBreachResult(z, b);
  }

  private sync(z: ZoneRuntime, to?: string): void {
    if (this.breach) z.net?.send({ ev: 'breach', ...this.breach }, to);
  }

  peerJoined(z: ZoneRuntime, peer: string): void {
    this.sync(z, peer);
  }

  message(z: ZoneRuntime, from: string, data: Record<string, unknown>): void {
    if (data.ev !== 'breach' || from !== z.world.hostPeer) return;
    const prev = this.breach;
    const next: BreachState = { start: Number(data.start), wave: Number(data.wave) || 0, done: data.done === true, failed: data.failed === true };
    this.breach = next;
    if ((next.done || next.failed) && !(prev?.start === next.start && (prev.done || prev.failed))) this.onBreachResult(z, next);
  }

  /** Every peer rewards its own player if it stood by the breach. */
  private onBreachResult(z: ZoneRuntime, b: BreachState): void {
    const prog = this.game.progression;
    const me = this.game.player;
    if (!b.done) {
      this.game.hud.announce('The Aether Breach closed before it was sealed.', 'warning');
      return;
    }
    this.game.hud.announce('Aether Breach sealed!', 'success');
    if (!prog || !me) return;
    const key = `breach_${b.start}`;
    const at = breachPoint(z);
    if (prog.char.flags[key] || Math.hypot(me.pos.x - at.x, me.pos.z - at.z) > BREACH_RADIUS) return;
    prog.char.flags[key] = true;
    prog.grantDrop(rollLoot('elite', { level: at.level, rng: prog.rng }), 'Aether Breach');
    prog.giveItem('aether_shard', 40 + at.level * 4);
  }

  dispose(): void {
    eventBanner.value = null;
  }
}

/** Every field zone gets the event script. */
export function worldEventsFor(game: Game, z: ZoneRuntime): WorldEventScript | null {
  const kind = z.layout.kind;
  if (kind !== 'field') return null;
  if (!EVENTS.some((d) => d.zones.includes(z.layout.id))) return null;
  return new WorldEventScript(game);
}

/** Toasts when an event starts in another zone the player could join. */
export function eventNotifier(game: Game): (g: Game, dt: number) => void {
  const told = new Set<string>();
  let t = 0;
  return (_g, dt) => {
    t -= dt;
    if (t > 0) return;
    t = 2;
    const prog = game.progression;
    if (!prog) return;
    const now = Date.now();
    for (const def of EVENTS) {
      const o = occurrence(def, now);
      const key = `${def.id}:${o.start}`;
      if (!o.active || told.has(key)) continue;
      told.add(key);
      if (o.zone === game.zoneId || prog.char.level < def.level - 5) continue;
      game.notify(`${def.name} is active in ${getZone(o.zone).name} (${fmtCountdown(o.end - now)} left)`, '#ffb84a');
    }
  };
}
